// Area icons (docs/61; AreaIcon.swift is the reference): an area's icon is the leading emoji of its title, as stored
// since the first version ("💼 Work"). Nothing else is stored: the editors show the emoji in a place of its own and
// write "emoji + space + name" back. The suggestions come from one word list in English and German,
// cases/area-icons.json; areaIconWords.ts is the web's copy, written by cases/tools/area_icons.py. The cases in the
// same file run against the kit, the web and the server.

import { AREA_ICON_WORDS } from "./areaIconWords";

/** The emoji an area gets when nothing suggests one for its name (Bastian, 2026-10-20). */
export const DEFAULT_AREA_EMOJI: string = AREA_ICON_WORDS.default;

/** Emoji people often choose for an area, offered beside the suggestions. */
export const OFTEN_USED_AREA_EMOJI: readonly string[] = AREA_ICON_WORDS.oftenUsed;

/** The most suggestions offered for a name. */
export const SUGGESTION_LIMIT = 8;

const graphemes = new Intl.Segmenter(undefined, { granularity: "grapheme" });

/**
 * Whether a grapheme cluster is an emoji: one shown as an emoji by default (flags too); one with the emoji form asked
 * for (U+FE0F, as in "🗂️") or a keycap ("1️⃣"); or a pictograph past Latin written without that form ("✈"). A plain
 * digit, "#" or "*" counts as an emoji in Unicode's tables but is none here.
 */
export function isEmoji(cluster: string): boolean {
  const first = cluster.codePointAt(0);
  if (first === undefined) return false;
  const scalar = String.fromCodePoint(first);
  if (/\p{Emoji_Presentation}/u.test(scalar)) return true;
  if (!/\p{Emoji}/u.test(scalar)) return false;
  if (cluster.includes("️") || cluster.includes("⃣")) return true;
  return first >= 0x2000;
}

/**
 * The title's leading emoji, and the name after it without the space between. A title that is only an emoji has the
 * name "". A title that starts with anything else (a digit, "#", a letter) has no emoji and keeps its name as it is.
 */
export function splitEmoji(title: string): { emoji?: string; name: string } {
  const first = graphemes.segment(title)[Symbol.iterator]().next();
  if (first.done || !isEmoji(first.value.segment)) return { name: title };
  return { emoji: first.value.segment, name: title.slice(first.value.segment.length).trim() };
}

/** The title an icon and a name make: "💼 Work", the name alone without an emoji, the emoji alone without a name. */
export function joinEmoji(emoji: string | undefined | null, name: string): string {
  const trimmed = name.trim();
  if (!emoji) return trimmed;
  return trimmed ? `${emoji} ${trimmed}` : emoji;
}

// MARK: - Suggestions

interface Keyword {
  tokens: string[];
  /** Written with "*": its last word matches every word starting with it. */
  isPrefix: boolean;
}

const SUFFIXES = ["s", "es", "e", "en", "n", "er"];

/**
 * The words of a text as the word list compares them: lower case, ß as ss, without marks (Büro → buro), cut at
 * everything that is not a letter or a digit.
 */
export function iconWords(text: string): string[] {
  const folded = text.toLowerCase().replaceAll("ß", "ss").normalize("NFD").replace(/\p{M}/gu, "");
  return folded.split(/[^\p{L}\p{N}]+/u).filter((word) => word.length > 0);
}

function matches(word: string, token: string, prefix: boolean): boolean {
  if (word === token) return true;
  if (prefix) return word.startsWith(token);
  if ([...token].length < 4 || !word.startsWith(token)) return false;
  return SUFFIXES.includes(word.slice(token.length));
}

function position(keyword: Keyword, words: readonly string[]): number | undefined {
  const { tokens } = keyword;
  for (let start = 0; start + tokens.length <= words.length; start += 1) {
    if (tokens.every((token, offset) => matches(words[start + offset] ?? "", token, keyword.isPrefix && offset === tokens.length - 1))) return start;
  }
  return undefined;
}

const PREPARED: readonly Keyword[][] = AREA_ICON_WORDS.topics.map((topic) => topic.words.map((word) => {
  const isPrefix = word.endsWith("*");
  return { tokens: iconWords(isPrefix ? word.slice(0, -1) : word), isPrefix };
}));

/**
 * Up to eight emoji that fit an area's name, best first; empty when nothing in the word list matches. A leading emoji
 * of the name is left out of the matching. How words match: cases/area-icons.json.
 */
export function areaIconSuggestions(name: string): string[] {
  const words = iconWords(splitEmoji(name).name);
  if (words.length === 0) return [];
  const found: { topic: number; length: number; position: number }[] = [];
  PREPARED.forEach((keywords, topic) => {
    let best: { length: number; position: number } | undefined;
    for (const keyword of keywords) {
      const at = position(keyword, words);
      if (at === undefined) continue;
      const length = keyword.tokens.length;
      if (best && (best.length > length || (best.length === length && best.position <= at))) continue;
      best = { length, position: at };
    }
    if (best) found.push({ topic, ...best });
  });
  // Found by more words first, then the one earlier in the name, then the list's order.
  found.sort((a, b) => b.length - a.length || a.position - b.position || a.topic - b.topic);
  const topics = AREA_ICON_WORDS.topics;
  const ordered = [
    ...found.flatMap(({ topic }) => topics[topic]?.emoji.slice(0, 1) ?? []),
    ...found.flatMap(({ topic }) => topics[topic]?.emoji.slice(1) ?? []),
  ];
  const result: string[] = [];
  for (const emoji of ordered) {
    if (result.includes(emoji)) continue;
    result.push(emoji);
    if (result.length === SUGGESTION_LIMIT) break;
  }
  return result;
}

/** The first suggestion for a name, else the default (🗂️): the icon an area gets when the user doesn't choose. */
export function suggestedOrDefault(name: string): string {
  return areaIconSuggestions(name)[0] ?? DEFAULT_AREA_EMOJI;
}

/** A title with an icon: the title itself when it has one, else the suggestion for it (or the default) before it. */
export function withAreaIcon(title: string): string {
  return splitEmoji(title).emoji !== undefined ? title : joinEmoji(suggestedOrDefault(title), title);
}
